/* AdminActivityHistory — iter100.248
 *
 * Full date-wise activity log for /admin. Deep-linkable from the
 * "View all →" affordance on the Overview page's Recent Activity
 * section. Covers the 5 customer-event sources (leads, messages,
 * applications, appointments, patient signups) — the admin audit
 * trail lives on a separate route (/admin/audit).
 *
 * UI grammar mirrors the Overview page:
 *   • rounded-2xl card shell + teal eyebrow via AdminSectionShell
 *   • FilterSearchBar + FilterChipRow for the source filter
 *   • Compact date range picker + pagination footer
 *
 * Backend: GET /api/admin/activity/history
 *   ?since=ISO&until=ISO&sources=csv&search=str&limit=N&skip=N
 * Returns { items: [...], total: N }
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    ChatCircleDots, EnvelopeSimple, UserPlus, CalendarBlank, Briefcase,
    Pulse, ArrowLeft, CaretLeft, CaretRight, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import AdminSectionShell from "@/components/admin/AdminSectionShell";

/* Icon + tone per source — matches the Overview page's grammar so
   admins recognise event types across surfaces. */
const SOURCE_META = {
    lead:             { Icon: ChatCircleDots,  tone: "text-rose-600",    label: "Lead",     detailHref: (id) => id ? `/admin/leads/web/${id}`         : "/admin/leads?tab=web-leads" },
    message:          { Icon: EnvelopeSimple,  tone: "text-indigo-600",  label: "Message",  detailHref: () => "/admin/messages" },
    application:      { Icon: Briefcase,       tone: "text-amber-700",   label: "App",      detailHref: (id) => id ? `/admin/leads/application/${id}` : "/admin/leads?tab=application" },
    appointment:      { Icon: CalendarBlank,   tone: "text-emerald-700", label: "Appt",     detailHref: () => "/admin/appointments" },
    "patient-signup": { Icon: UserPlus,        tone: "text-teal-700",    label: "Patient",  detailHref: (id) => id ? `/admin/patients/${id}`          : "/admin/patients" },
};

const SOURCE_ORDER = ["lead", "message", "application", "appointment", "patient-signup"];

/* Date range preset buttons. Compute since/until on click; empty until
   = "up to now" — no upper bound sent. */
const DATE_PRESETS = [
    { id: "today", label: "Today",   days: 1 },
    { id: "7d",    label: "Last 7 days",  days: 7 },
    { id: "30d",   label: "Last 30 days", days: 30 },
    { id: "90d",   label: "Last 90 days", days: 90 },
    { id: "all",   label: "All time",     days: null },
];

const PAGE_SIZE = 50;

function isoDaysAgo(days) {
    if (!days) return "";
    const d = new Date();
    d.setDate(d.getDate() - days);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
}

function relativeTime(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (isNaN(then)) return "";
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

export default function AdminActivityHistory() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [preset, setPreset] = useState(() => searchParams.get("preset") || "7d");
    const [sources, setSources] = useState(() => {
        const s = searchParams.get("sources");
        return s ? new Set(s.split(",").filter(Boolean)) : new Set(SOURCE_ORDER);
    });
    const [search, setSearch] = useState(() => searchParams.get("q") || "");
    const [page, setPage] = useState(() => parseInt(searchParams.get("page") || "1", 10) || 1);
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const activePreset = useMemo(
        () => DATE_PRESETS.find((p) => p.id === preset) || DATE_PRESETS[1],
        [preset]
    );
    const sinceIso = useMemo(() => isoDaysAgo(activePreset.days), [activePreset]);

    /* Keep the URL in sync so an admin can bookmark or share a filter set. */
    useEffect(() => {
        const params = new URLSearchParams();
        if (preset !== "7d") params.set("preset", preset);
        if (sources.size !== SOURCE_ORDER.length) params.set("sources", [...sources].join(","));
        if (search) params.set("q", search);
        if (page > 1) params.set("page", String(page));
        setSearchParams(params, { replace: true });
    }, [preset, sources, search, page, setSearchParams]);

    /* Reset page whenever a filter changes so the admin never lands on
       page 4 of an empty result set. */
    useEffect(() => {
        setPage(1);
    }, [preset, sources, search]);

    useEffect(() => {
        setLoading(true);
        const params = {
            limit: PAGE_SIZE,
            skip: (page - 1) * PAGE_SIZE,
        };
        if (sinceIso) params.since = sinceIso;
        if (sources.size < SOURCE_ORDER.length) params.sources = [...sources].join(",");
        if (search.trim()) params.search = search.trim();
        api.get("/admin/activity/history", { params })
            .then((r) => {
                setItems(r.data.items || []);
                setTotal(r.data.total || 0);
            })
            .catch(() => {
                setItems([]);
                setTotal(0);
            })
            .finally(() => setLoading(false));
    }, [page, sinceIso, sources, search]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    function toggleSource(id) {
        const next = new Set(sources);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        if (next.size === 0) return; // Never allow an empty set — noop.
        setSources(next);
    }
    function selectAllSources() { setSources(new Set(SOURCE_ORDER)); }

    return (
        <div className="space-y-6" data-testid="admin-activity-history-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <Link
                        to="/admin"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors mb-2 group"
                        data-testid="activity-history-back"
                    >
                        <ArrowLeft size={12} weight="bold" className="group-hover:-translate-x-0.5 transition-transform" />
                        Back to Overview
                    </Link>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Activity history</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Full log of customer events — leads, messages, applications, appointments, and patient signups.
                    </p>
                </div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground tabular-nums" data-testid="activity-history-total">
                    {loading ? "Loading…" : `${total} event${total === 1 ? "" : "s"}`}
                </div>
            </div>

            {/* Filters card */}
            <AdminSectionShell
                eyebrow="Filters"
                tone="teal"
                description="Narrow by date range, event type, or search."
                bodyPad
                testid="activity-history-filters"
            >
                {/* Date range presets */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground mr-1">Range</span>
                    {DATE_PRESETS.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setPreset(p.id)}
                            className={`h-8 px-3 rounded-full text-[11.5px] font-semibold transition-colors ${
                                preset === p.id
                                    ? "bg-teal-600 text-white"
                                    : "bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground"
                            }`}
                            data-testid={`activity-history-preset-${p.id}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* Source chips */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground mr-1">Sources</span>
                    {SOURCE_ORDER.map((id) => {
                        const meta = SOURCE_META[id];
                        const on = sources.has(id);
                        const { Icon } = meta;
                        return (
                            <button
                                key={id}
                                type="button"
                                onClick={() => toggleSource(id)}
                                className={`h-8 px-3 rounded-full text-[11.5px] font-semibold transition-colors inline-flex items-center gap-1.5 ${
                                    on
                                        ? "bg-teal-600 text-white"
                                        : "bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground"
                                }`}
                                data-testid={`activity-history-source-${id}`}
                            >
                                <Icon size={11} weight={on ? "fill" : "duotone"} />
                                {meta.label}
                            </button>
                        );
                    })}
                    {sources.size < SOURCE_ORDER.length && (
                        <button
                            type="button"
                            onClick={selectAllSources}
                            className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                            data-testid="activity-history-source-reset"
                        >
                            Select all
                        </button>
                    )}
                </div>

                {/* Search */}
                <div className="relative max-w-md">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, subject, source detail…"
                        className="w-full pl-9 pr-9 h-9 rounded-full bg-secondary/40 border border-transparent focus:border-teal-500 focus:bg-card text-sm outline-none transition"
                        data-testid="activity-history-search"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label="Clear search"
                        >
                            <X size={13} weight="bold" />
                        </button>
                    )}
                </div>
            </AdminSectionShell>

            {/* Results */}
            <AdminSectionShell
                eyebrow="Events"
                tone="teal"
                description={loading ? "Loading…" : `Showing page ${page} of ${totalPages}.`}
                right={
                    total > 0 && (
                        <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground tabular-nums">
                            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                        </span>
                    )
                }
                bodyPad
                testid="activity-history-results"
            >
                {loading && items === null && (
                    <div className="text-muted-foreground text-sm py-6 text-center">Loading events…</div>
                )}
                {items !== null && items.length === 0 && (
                    <div className="text-muted-foreground text-sm py-6 text-center flex flex-col items-center gap-2">
                        <ChatCircleDots size={22} weight="duotone" className="text-muted-foreground/50" />
                        No events match these filters.
                    </div>
                )}
                {items !== null && items.length > 0 && (
                    <div className="divide-y divide-border/60 -mx-1">
                        {items.map((ev, idx) => {
                            const meta = SOURCE_META[ev.source] || { Icon: Pulse, tone: "text-primary", label: "Event", detailHref: () => "/admin" };
                            const { Icon } = meta;
                            const to = meta.detailHref(ev.id);
                            return (
                                <Link
                                    to={to}
                                    key={`${ev.source}-${ev.id || idx}`}
                                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/50 transition-colors"
                                    data-testid={`activity-history-item-${idx}`}
                                >
                                    <div className="shrink-0 mt-0.5">
                                        <Icon size={16} weight="duotone" className={meta.tone} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{ev.title}</div>
                                        <div className="text-xs text-muted-foreground truncate">{ev.subtitle}</div>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <div className="text-xs text-muted-foreground whitespace-nowrap">{relativeTime(ev.created_at)}</div>
                                        <div className="text-[10.5px] text-muted-foreground/70 whitespace-nowrap tabular-nums" title={ev.created_at}>{formatDate(ev.created_at)}</div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}

                {/* Pagination footer */}
                {total > PAGE_SIZE && (
                    <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs text-muted-foreground">
                            Page <span className="font-semibold text-foreground">{page}</span> of <span className="font-semibold text-foreground">{totalPages}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="rounded-full h-8 px-3"
                                data-testid="activity-history-prev"
                            >
                                <CaretLeft size={12} weight="bold" className="mr-1" /> Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                className="rounded-full h-8 px-3"
                                data-testid="activity-history-next"
                            >
                                Next <CaretRight size={12} weight="bold" className="ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </AdminSectionShell>
        </div>
    );
}
